import { useMutation, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";

import { Button, Dialog, SelectField, SkeletonRows, TextField } from "@/components/ui";
import { formatMoney } from "@/lib/format";
import { Decimal } from "@/lib/money";
import { hierarchyRepository } from "@/repositories/hierarchyRepository";
import { procurementRepository, type PurchaseOrder } from "@/repositories/procurementRepository";
import { useAuthStore } from "@/stores/authStore";

/**
 * Recording what actually arrived.
 *
 * Quantities default to what is still outstanding, because that is what usually
 * turns up and a form that made somebody retype the expected figure would be
 * retyped wrong. Everything is editable, because short deliveries are the reason
 * the receipt is a separate document from the order.
 *
 * The idempotency key is minted once, when the dialog opens, and reused for
 * every attempt. A key generated inside the submit handler would be new on each
 * retry — and a delivery recorded twice puts stock on a shelf that does not have
 * it, which surfaces weeks later as an unexplained shortage.
 */
export function ReceiveDialog({
  order,
  onClose,
  onReceived,
}: {
  order: PurchaseOrder;
  onClose: () => void;
  onReceived: () => void;
}) {
  const [quantities, setQuantities] = useState<Record<string, string>>({});
  const [sectionId, setSectionId] = useState("");
  const [supplierRef, setSupplierRef] = useState("");
  const [idempotencyKey] = useState(() => crypto.randomUUID());

  const activeSectionId = useAuthStore((s) => s.session?.activeSectionId ?? "");

  const detail = useQuery({
    queryKey: ["procurement", "orders", order.id],
    queryFn: () => procurementRepository.order(order.id),
  });

  const sections = useQuery({
    queryKey: ["hierarchy", "sections"],
    queryFn: () => hierarchyRepository.sectionsForUser(),
  });

  const outstanding = useMemo(
    () => (detail.data?.outstanding ?? []).filter((line) => Number(line.outstanding_units) > 0),
    [detail.data],
  );

  // The default is the outstanding quantity, resolved once the order loads.
  const effective = (lineId: string, fallback: string) => quantities[lineId] ?? fallback;

  const total = useMemo(
    () =>
      outstanding.reduce((sum, line) => {
        const quantity = Number(effective(line.line_id, line.outstanding_units));
        if (!Number.isFinite(quantity) || quantity <= 0) return sum;
        return sum.plus(new Decimal(line.unit_cost || "0").times(quantity));
      }, new Decimal(0)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [outstanding, quantities],
  );

  const chosenSection = sectionId || activeSectionId || sections.data?.[0]?.sectionId || "";

  const receive = useMutation({
    mutationFn: () =>
      procurementRepository.receive(
        {
          order_id: order.id,
          supplier_id: order.supplier,
          section_id: chosenSection,
          supplier_reference: supplierRef.trim(),
          lines: outstanding
            .map((line) => ({
              product_id: line.product_id,
              quantity: effective(line.line_id, line.outstanding_units),
              unit_cost: line.unit_cost,
              order_line_id: line.line_id,
            }))
            .filter((line) => Number(line.quantity) > 0),
        },
        idempotencyKey,
      ),
    onSuccess: onReceived,
  });

  const anything = outstanding.some(
    (line) => Number(effective(line.line_id, line.outstanding_units)) > 0,
  );

  return (
    <Dialog
      open
      onClose={onClose}
      size="lg"
      title={`Receive ${order.reference}`}
      description={`From ${order.supplier_name}. What is not on the van stays outstanding.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={receive.isPending}
            disabled={!anything || !chosenSection}
            onClick={() => receive.mutate()}
          >
            Receive {formatMoney(total.toFixed(2))}
          </Button>
        </>
      }
    >
      {detail.isLoading ? (
        <SkeletonRows rows={5} />
      ) : (
        <div className="space-y-4">
          {outstanding.length === 0 ? (
            <p className="text-sm text-ink-600">Everything on this order has already arrived.</p>
          ) : (
            <ul className="divide-y divide-line">
              {outstanding.map((line) => (
                <li key={line.line_id} className="flex flex-wrap items-center gap-3 py-3">
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium text-ink-900">
                      {line.description}
                    </span>
                    <span className="block text-xs text-ink-500">
                      {line.outstanding_units} outstanding of {line.ordered_units} ·{" "}
                      {formatMoney(line.unit_cost)} each
                    </span>
                  </span>

                  <label className="flex items-center gap-2">
                    <span className="sr-only">How many of {line.description} arrived</span>
                    <input
                      type="number"
                      min="0"
                      step="1"
                      inputMode="decimal"
                      value={effective(line.line_id, line.outstanding_units)}
                      onChange={(event) =>
                        setQuantities((current) => ({
                          ...current,
                          [line.line_id]: event.target.value,
                        }))
                      }
                      className="h-9 w-24 rounded-control border border-line bg-surface px-2 text-sm tabular-nums text-ink-900 outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20"
                    />
                  </label>
                </li>
              ))}
            </ul>
          )}

          <SelectField
            label="Which counter does it go to?"
            value={chosenSection}
            onChange={(event) => setSectionId(event.target.value)}
            options={(sections.data ?? []).map((section) => ({
              value: section.sectionId,
              label: section.sectionName,
            }))}
            hint="Stock lands here. A shop with one counter has only one choice."
          />

          <TextField
            label="Their invoice or delivery note number"
            value={supplierRef}
            maxLength={60}
            onChange={(event) => setSupplierRef(event.target.value)}
            placeholder="INV-88213"
            hint="So the paper on the spike can be matched to this delivery."
          />

          {receive.error != null && (
            <p className="text-sm text-danger-700">
              {receive.error instanceof Error
                ? receive.error.message
                : "That delivery could not be recorded."}
            </p>
          )}
        </div>
      )}
    </Dialog>
  );
}
